/**
 * Intake Service
 * AIDLC Administrative Backend
 *
 * Reads the requirements intakes the CRM sent for a project (each with where
 * its requirements stand) and the cost roll-up: estimate against actual by
 * project, by customer, by agent within each project, and for the unit.
 */

import apiClient from './api';

export interface TokenEstimateView {
  inputTokens: number;
  outputTokens: number;
  estimatedCost: number;
  estimatedDuration: number;
  taskClass: string;
  confidence: 'low' | 'medium' | 'high';
  model: string;
  reasoning: string;
}

export interface Spend {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  totalTokens: number;
  costUsd: number;
  sessions: number;
}

export interface Estimated {
  items: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
}

export interface IntakeRequirementRow {
  externalRef: string;
  ref: string;
  itemId: string;
  title: string;
  status: string;
  withdrawn: boolean;
  assignedAgent: string | null;
  estimate: TokenEstimateView | null;
  baselineEstimate: TokenEstimateView | null;
  actual: Spend;
}

export interface IntakePacket {
  externalRef: string;
  number: string;
  title: string;
  customerName: string;
  revision: number;
  approvedBy: string | null;
  approvedOn: string | null;
  questionnaireVersion: string | null;
  sections: Array<{ key: string; title: string; answers: Array<{ key: string; question: string; answer: string; answeredBy: string | null }> }>;
  stakeholders: Array<{ name: string; title: string | null; role: string | null }>;
  receivedAt: string;
  updatedAt: string;
}

export interface IntakeStatus {
  intake: IntakePacket;
  project: { id: string; name: string; aidlcState: string; currentPhase: string | null };
  requirements: IntakeRequirementRow[];
  estimated: { requirements: number; inputTokens: number; outputTokens: number; totalTokens: number; costUsd: number; durationMinutes: number };
  actual: { totalTokens: number; costUsd: number; sessions: number };
}

export interface AgentSpend extends Spend { agentId: string; agentName: string }

export interface ProjectRollup {
  projectId: string;
  projectName: string;
  aidlcState: string;
  crmCustomerId: string | null;
  customerName: string | null;
  revenueUsd: number | null;
  actual: Spend;
  estimated: Estimated;
  varianceUsd: number | null;
  openItems: number;
  doneItems: number;
  byAgent: AgentSpend[];
}

export interface CustomerRollup {
  crmCustomerId: string;
  customerName: string;
  projects: number;
  revenueUsd: number | null;
  actual: Spend;
  estimated: Estimated;
  varianceUsd: number | null;
  marginUsd: number | null;
}

export interface AgentRollup extends AgentSpend {
  byProject: Array<Spend & { projectId: string | null; projectName: string }>;
}

export interface CostRollup {
  generatedAt: string;
  unit: { actual: Spend; estimated: Estimated; revenueUsd: number | null; projects: number; customers: number };
  customers: CustomerRollup[];
  internal: { projects: number; actual: Spend; estimated: Estimated };
  unattributed: Spend;
  projects: ProjectRollup[];
  agents: AgentRollup[];
}

export async function getProjectIntakes(projectId: string): Promise<IntakeStatus[]> {
  const response = await apiClient.get<IntakeStatus[]>(`/projects/${projectId}/intakes`);
  return response.data;
}

export async function getCostRollup(): Promise<CostRollup> {
  const response = await apiClient.get<CostRollup>('/tokens/rollup');
  return response.data;
}
